// frontend/src/pages/Help.js
import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AspectRatio,
  Badge,
  Box,
  Container,
  Heading,
  HStack,
  Icon,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { FiHelpCircle, FiSearch, FiInfo } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { HELP_SECTIONS } from '../content/helpContent';

const helpAsset = (name) => `/help/${name}`;

const normalize = (t) =>
  String(t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

function guideMatches(guide, query) {
  if (!query) return true;
  const haystack = normalize(
    [guide.title, guide.summary, ...(guide.steps || []).map(s => s.text), ...(guide.tips || [])].join(' ')
  );
  return query.split(/\s+/).every(word => haystack.includes(word));
}

const Screenshot = ({ file, alt, onOpen }) => {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <Image
      src={helpAsset(file)}
      alt={alt}
      mt={3}
      borderWidth="1px"
      borderRadius="md"
      maxH="380px"
      cursor="zoom-in"
      onClick={() => onOpen(file, alt)}
      onError={() => setFailed(true)}
    />
  );
};

const GuideVideo = ({ video, title }) => {
  if (!video) return null;
  if (video.youtube) {
    return (
      <AspectRatio ratio={16 / 9} maxW="720px" mb={4}>
        <iframe
          title={title}
          src={`https://www.youtube-nocookie.com/embed/${encodeURIComponent(video.youtube)}`}
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </AspectRatio>
    );
  }
  if (video.src) {
    return (
      <Box mb={4} maxW="720px">
        <video controls preload="metadata" style={{ width: '100%', borderRadius: 8 }}>
          <source src={helpAsset(`videos/${video.src}`)} type="video/mp4" />
        </video>
      </Box>
    );
  }
  return null;
};

const Guide = ({ guide, onOpenImage }) => (
  <AccordionItem>
    <h3>
      <AccordionButton py={4}>
        <Box flex="1" textAlign="left">
          <Text fontWeight="600">{guide.title}</Text>
          {guide.summary && (
            <Text fontSize="sm" color="gray.500" fontWeight="normal">
              {guide.summary}
            </Text>
          )}
        </Box>
        {guide.video && <Badge colorScheme="purple" mr={2}>Vidéo</Badge>}
        <AccordionIcon />
      </AccordionButton>
    </h3>
    <AccordionPanel pb={5}>
      <GuideVideo video={guide.video} title={guide.title} />
      <VStack align="stretch" spacing={4}>
        {(guide.steps || []).map((step, i) => (
          <HStack key={i} align="flex-start" spacing={3}>
            <Box
              minW="26px"
              h="26px"
              borderRadius="full"
              bg="eecf.500"
              color="white"
              fontSize="sm"
              fontWeight="600"
              display="flex"
              alignItems="center"
              justifyContent="center"
              mt="1px"
            >
              {i + 1}
            </Box>
            <Box flex="1">
              <Text>{step.text}</Text>
              {step.image && <Screenshot file={step.image} alt={step.text} onOpen={onOpenImage} />}
            </Box>
          </HStack>
        ))}
      </VStack>
      {(guide.tips || []).map((tip, i) => (
        <HStack key={i} mt={4} p={3} bg="blue.50" borderRadius="md" align="flex-start">
          <Icon as={FiInfo} color="blue.500" mt={1} />
          <Text fontSize="sm" color="blue.900">{tip}</Text>
        </HStack>
      ))}
    </AccordionPanel>
  </AccordionItem>
);

export default function Help() {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [preview, setPreview] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const roles = useMemo(() => {
    const r = new Set(['membre']);
    if (user?.role) r.add(user.role);
    if (user?.is_admin) r.add('admin');
    return r;
  }, [user]);

  const query = normalize(search).trim();

  const sections = useMemo(
    () =>
      HELP_SECTIONS
        .filter(section => section.audiences.some(a => roles.has(a)))
        .map(section => ({ ...section, guides: section.guides.filter(g => guideMatches(g, query)) }))
        .filter(section => section.guides.length > 0),
    [roles, query]
  );

  const openImage = (file, alt) => {
    setPreview({ file, alt });
    onOpen();
  };

  return (
    <Container maxW="container.lg" py={6}>
      <HStack mb={2} spacing={3}>
        <Icon as={FiHelpCircle} boxSize={7} color="eecf.500" />
        <Heading size="lg">Aide et guides d'utilisation</Heading>
      </HStack>
      <Text color="gray.600" mb={5}>
        Retrouvez ici, pas à pas, comment utiliser l'application selon votre rôle.
        Pour toute autre question, écrivez à{' '}
        <a href="mailto:accueil@eecf-paris.fr" style={{ color: '#1F7040' }}>accueil@eecf-paris.fr</a>.
      </Text>

      <InputGroup mb={5}>
        <InputLeftElement pointerEvents="none">
          <Icon as={FiSearch} color="gray.400" />
        </InputLeftElement>
        <Input
          placeholder="Rechercher dans l'aide (ex. paiement, reçu, mot de passe)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          bg="white"
        />
      </InputGroup>

      {sections.length === 0 ? (
        <Text color="gray.500">Aucun guide ne correspond à votre recherche.</Text>
      ) : (
        <Tabs variant="enclosed" colorScheme="green" isLazy key={query ? 'search' : 'all'}>
          <TabList overflowX="auto" overflowY="hidden">
            {sections.map(section => (
              <Tab key={section.id} whiteSpace="nowrap">{section.title}</Tab>
            ))}
          </TabList>
          <TabPanels bg="white" borderWidth="1px" borderTop="0" borderBottomRadius="md">
            {sections.map(section => (
              <TabPanel key={section.id} px={{ base: 2, md: 4 }}>
                <Accordion allowMultiple defaultIndex={query ? section.guides.map((_, i) => i) : []}>
                  {section.guides.map(guide => (
                    <Guide key={guide.id} guide={guide} onOpenImage={openImage} />
                  ))}
                </Accordion>
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      )}

      <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
        <ModalOverlay />
        <ModalContent bg="transparent" boxShadow="none">
          <ModalCloseButton color="white" zIndex={2} />
          {preview && (
            <Image
              src={helpAsset(preview.file)}
              alt={preview.alt}
              maxH="90vh"
              objectFit="contain"
              borderRadius="md"
              bg="white"
            />
          )}
        </ModalContent>
      </Modal>
    </Container>
  );
}
